/**
 * Hooks for reading the current user's workspace role and gating UI.
 *
 * Single source of truth: the `userRole` field on the current Workspace
 * (already exposed by the backend on `GET /api/workspace`). Backend
 * middleware is the security boundary; these hooks exist only to hide
 * write controls so viewers don't see buttons that would 403 when
 * clicked.
 */

import { useWorkspace } from '../../contexts/WorkspaceContext';

export type WorkspaceRole = 'OWNER' | 'ADMIN' | 'MEMBER' | 'GUEST' | 'VIEWER';

const VALID_ROLES: ReadonlySet<string> = new Set(['OWNER', 'ADMIN', 'MEMBER', 'GUEST', 'VIEWER']);

/**
 * Shared with `useCanUseBird`. Kept here — and this module kept free of any
 * `AuthContext` import — because 25+ modules pull `useWorkspaceRole` in. An
 * `AuthContext` edge here reaches `config/firebase`, which calls `initializeAuth()` at
 * module scope, so every suite that mocks only `WorkspaceContext` would die on
 * `auth/invalid-api-key` before a single test ran (it took out WriterRoute,
 * Projects and ProfileTab). Keep the auth dependency in `useCanUseBird.ts`,
 * where the tests that need it already know to mock it.
 */
export function normaliseWorkspaceRole(raw: unknown): WorkspaceRole | null {
  if (typeof raw !== 'string') return null;
  const upper = raw.toUpperCase();
  return VALID_ROLES.has(upper) ? (upper as WorkspaceRole) : null;
}

/**
 * The current user's role in the active workspace, or `null` when the
 * workspace is still loading or the role hasn't been resolved yet.
 *
 * Always uppercase so callers can compare against the Prisma `Role` enum
 * directly. Falls back to `null` (not 'VIEWER') so first-paint UI doesn't
 * flash hidden controls before the workspace loads.
 */
export function useWorkspaceRole(): WorkspaceRole | null {
  const { workspace } = useWorkspace();
  return normaliseWorkspaceRole(workspace?.userRole);
}

/**
 * Returns true when the current user holds a read-only workspace role —
 * `GUEST` (the live read-only role) or `VIEWER` (deprecated, kept only
 * for backwards compatibility). Both map to VIEW permission on the
 * backend; the UI must never distinguish them (see
 * `.claude/rules/entity-acl.md`). Used to:
 *   - hide create / edit / delete buttons
 *   - disable form inputs
 *   - hide the write-only composer controls in Bird chat (Live, fallback
 *     dictation) — the Chat entry point itself is gated by `useCanUseBird`,
 *     which admits read-only roles
 *   - hide admin-style nav (Trash, Audit logs)
 *
 * Returns `false` while the role is still loading so we don't flash a
 * disabled state for legitimate writers on first paint. The backend
 * still enforces the 403/404 if a read-only user somehow reaches a
 * write endpoint.
 *
 * This is the ONLY read-only write-gate. A `role === 'VIEWER'` shortcut
 * would silently grant GUESTs write controls — the 2026-07-05 incident.
 */
export function useIsReadOnly(): boolean {
  return isReadOnlyRole(useWorkspaceRole());
}

/**
 * The read-only predicate as a pure function, for the callers that cannot use
 * the hook — onboarding snapshots the cohort while the role may still be
 * loading, and `useIsReadOnly` deliberately collapses "loading" to `false`,
 * which would latch a guest as a writer.
 *
 * Both must handle GUEST **and** the deprecated VIEWER: a `role === 'VIEWER'`
 * shortcut silently grants GUESTs write controls — the 2026-07-05 incident.
 * Keeping one implementation is what stops the two drifting.
 */
export function isReadOnlyRole(role: WorkspaceRole | null): boolean {
  return role === 'GUEST' || role === 'VIEWER';
}

/**
 * Convenience for callers that want the inverse — true when the user is
 * allowed to mutate the workspace.
 */
export function useCanWrite(): boolean {
  const role = useWorkspaceRole();
  return role === 'OWNER' || role === 'ADMIN' || role === 'MEMBER';
}

// There is deliberately no `useIsOwner`. The single-workspace endpoints set
// `userRole` from the auth middleware, which maps OWNER to 'admin', so an
// owner check here is false for every real owner. Owner-only UI reads the
// member's role from the workspace members list instead (see MembersTab).
